/**
 * Chart colors for Program Insights (effort histogram + sets by muscle groups).
 */

// --- Effort distribution (RPE 5–10 or RiR 5–0): 11 columns, six hues (pairs + top bin). ---

export const INSIGHTS_EFFORT_DISTRIBUTION_BAR_COLORS = Object.freeze([
  "#489FDE",
  "#489FDE",
  "#61C44D",
  "#61C44D",
  "#EDB930",
  "#EDB930",
  "#EC8C27",
  "#EC8C27",
  "#EB4F56",
  "#EB4F56",
  "#96999D",
]);

/**
 * @param {number} index — column index 0…10 (same bucket order for RPE and RiR charts).
 * @returns {string} hex color
 */
export function insightsEffortDistributionBarColorAtIndex(index) {
  const i = Math.max(0, Math.min(10, Math.floor(index)));
  return INSIGHTS_EFFORT_DISTRIBUTION_BAR_COLORS[i];
}

/** Trailing AMRAP bar on the RPE/RiR distribution chart — same gray used for the Rep Range chart's AMRAP bar. */
export const INSIGHTS_EFFORT_AMRAP_BAR_COLOR = "#6d727f";

// --- Sets by muscle groups: solid primary per row; secondary = same hue @ 0.3 over `--white-10` track. ---

/** @type {Readonly<Record<string, string>>} */
export const INSIGHTS_MUSCLE_BAR_PRIMARY_HEX = Object.freeze({
  biceps: "#DD5549",
  triceps: "#E9A32E",
  back: "#55B683",
  glutes: "#55B5A6",
  abductors: "#F0A030",
  adductors: "#E0509E",
  calves: "#ACB72B",
  quadriceps: "#5FB555",
  hamstrings: "#2F8FA8",
  abs: "#4E81F2",
  forearms: "#8560F1",
  chest: "#D579D5",
  shoulders: "#7C3062",
  // Legacy: deprecated "Legs" value kept for any stored data still keyed by it.
  legs: "#5FB555",
});

const MUSCLE_BAR_FALLBACK_PRIMARY = "#55B683";

/**
 * @param {string} hex — `#RRGGBB`
 * @returns {[number, number, number]}
 */
function parseHexRgb(hex) {
  const raw = String(hex).trim().replace("#", "");
  const expanded =
    raw.length === 3
      ? raw
          .split("")
          .map((c) => c + c)
          .join("")
      : raw;
  if (expanded.length !== 6) {
    return [85, 182, 131];
  }
  const r = Number.parseInt(expanded.slice(0, 2), 16);
  const g = Number.parseInt(expanded.slice(2, 4), 16);
  const b = Number.parseInt(expanded.slice(4, 6), 16);
  if (![r, g, b].every((n) => Number.isFinite(n))) {
    return [85, 182, 131];
  }
  return [r, g, b];
}

/**
 * @param {string} muscleId — chart row id (`biceps`, `triceps`, …).
 * @returns {string}
 */
export function muscleBarPrimaryBackground(muscleId) {
  return INSIGHTS_MUSCLE_BAR_PRIMARY_HEX[muscleId] ?? MUSCLE_BAR_FALLBACK_PRIMARY;
}

/**
 * @param {string} muscleId
 * @param {number} [alpha]
 * @returns {string} `rgba(...)`
 */
export function muscleBarSecondaryBackground(muscleId, alpha = 0.3) {
  const hex = muscleBarPrimaryBackground(muscleId);
  const [r, g, b] = parseHexRgb(hex);
  return `rgba(${r},${g},${b},${alpha})`;
}
